import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { AuthService } from 'app/core/auth/auth.service';
import {
  compactNavigation,
  defaultNavigation,
  futuristicNavigation,
  horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';

/** Remove an item (at any depth) from a navigation tree, in place. */
function removeNavigationItem(items: FuseNavigationItem[], id: string): void {
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].id === id) {
      items.splice(i, 1);
    } else if (items[i].children?.length) {
      removeNavigationItem(items[i].children, id);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
  private readonly _fuseMockApiService = inject(FuseMockApiService);
  private readonly _authService = inject(AuthService);

  private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
  private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
  private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
  private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

  /**
   * Constructor
   */
  constructor() {
    // Register Mock API handlers
    this.registerHandlers();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Register Mock API handlers
   */
  registerHandlers(): void {
    // -----------------------------------------------------------------------------------------------------
    // @ Navigation - GET
    // -----------------------------------------------------------------------------------------------------
    this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
      const roles = this._authService.getRoles();
      const permissions = this._authService.getPermissions();
      const isAdmin = roles.includes('AIVA_admin');

      const hasManagementPermission = permissions.some(
        permission => !permission.endsWith('_read') && !permission.endsWith('_read_any')
      );

      let localDefaultNavigation = cloneDeep(this._defaultNavigation);

      if (!isAdmin && !hasManagementPermission) {
        localDefaultNavigation = localDefaultNavigation.filter(item => item.id !== 'admin-settings');
      }

      // Fill compact navigation children using the default navigation
      this._compactNavigation.forEach((compactNavItem) => {
        this._defaultNavigation.forEach((defaultNavItem) => {
          if (defaultNavItem.id === compactNavItem.id) {
            compactNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Fill futuristic navigation children using the default navigation
      this._futuristicNavigation.forEach((futuristicNavItem) => {
        this._defaultNavigation.forEach((defaultNavItem) => {
          if (defaultNavItem.id === futuristicNavItem.id) {
            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      // Fill horizontal navigation children using the default navigation
      this._horizontalNavigation.forEach((horizontalNavItem) => {
        this._defaultNavigation.forEach((defaultNavItem) => {
          if (defaultNavItem.id === horizontalNavItem.id) {
            horizontalNavItem.children = cloneDeep(defaultNavItem.children);
          }
        });
      });

      const localCompactNavigation = cloneDeep(this._compactNavigation);
      const localFuturisticNavigation = cloneDeep(this._futuristicNavigation);
      const localHorizontalNavigation = cloneDeep(this._horizontalNavigation);

      // AIVA-2143: Usage & budget is for platform admins only.
      if (!this._authService.isPlatformAdmin()) {
        for (const navigation of [
          localDefaultNavigation,
          localCompactNavigation,
          localFuturisticNavigation,
          localHorizontalNavigation,
        ]) {
          removeNavigationItem(navigation, 'admin-settings.usage-budget');
        }
      }

      // Return the response
      return [
        200,
        {
          compact: localCompactNavigation,
          default: localDefaultNavigation,
          futuristic: localFuturisticNavigation,
          horizontal: localHorizontalNavigation,
        },
      ];
    });
  }
}
